import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import App from 'containers/app';
import EditPdf from 'containers/editPdf';
import { getLocale } from 'etc/i18nConfig';
import { Notifier, setNotifierTranslate } from 'factories/notificationFactory';
import { getUrlVars } from 'utils/utilities';
import { createMuiTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import 'react-notifications-component/dist/theme.css';
import userManager from 'utils/userManager';
import { IRootReduxState } from 'types/reducers';
import { useIntl } from 'react-intl';
import moment from 'moment';
import 'moment/locale/ko';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/zh-cn';

const IntlSync: React.FC = () => {
  const intl = useIntl()
  React.useEffect(() => {
    setNotifierTranslate((id, defaultMessage) => intl.formatMessage({ id, defaultMessage }))
  }, [intl])
  return null
}

const RootElement: React.FC = () => {
  const user = useSelector((state: IRootReduxState) => state.oidc.user)
  const isLoadingUser = useSelector((state: IRootReduxState) => state.oidc.isLoadingUser)
  const theme = useSelector((state: IRootReduxState) => state.app.user.theme)
  const localeCode = useSelector((state: IRootReduxState) => state.app.user.localeCode)

  // Guard: wait until OidcProvider has finished loading the user from storage before
  // deciding whether to redirect. Without this guard the check fires on the very first
  // render (isLoadingUser=true, user=null) and immediately redirects a valid session
  // back to the login page — a race condition introduced by async storage hydration.
  React.useEffect(() => {
    if (isLoadingUser) return;

    const excludedLocationPaths = ['/', '/callback', '/bye', '/auth/callback', '/sso-relay', '/reset-password', '/login/2fa'];
    if (!window.location || excludedLocationPaths.includes(window.location.pathname)) return;

    if (!user || user.expired) {
      localStorage.setItem('workaware-requested-pathname', btoa(window.location.pathname + window.location.search));
    }

    if (user && user.expires_at && user.expires_at * 1000 < Date.now()) {
      localStorage.setItem('workaware-requested-pathname', btoa(window.location.pathname + window.location.search));
      window.location.replace('/');
    }
  }, [user, isLoadingUser]);

  const locale = getLocale(localeCode);

  // Set synchronously so children that run moment() during render pick up the right locale
  const momentLocale = locale.code === 'zh' ? 'zh-cn' : locale.code;
  moment.locale(momentLocale);

  React.useEffect(() => {
    document.documentElement.lang = locale.code;
  }, [locale.code]);

  const appTheme = React.useMemo(() => {
    const isDark = theme === 'dark';
    return createMuiTheme({
      overrides: {
        MuiGrid: {
          root: {
            '& .MuiGrid-item': {
              padding: 0
            },
            '& .MuiInput-underline:before': {
              borderColor: isDark ? '#3a4452' : '#ddd'
            }
          }
        },
        MuiCssBaseline: {
          '@global': {
            body: {
              backgroundColor: isDark ? '#0f172a' : '#fafafa',
              color: isDark ? '#e4e7ec' : 'inherit',
              transition: 'background-color 200ms ease',
            },
          },
        },
        MuiPickersBasePicker: {
          container: {
            backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
          },
          pickerView: {
            backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
          },
        },
        MuiPickersToolbar: {
          toolbar: {
            backgroundColor: isDark ? '#0f172a' : '#1565C0',
          },
        },
        MuiPickersToolbarText: {
          toolbarTxt: {
            color: isDark ? '#94a3b8' : 'rgba(255,255,255,0.54)',
          },
          toolbarBtnSelected: {
            color: '#ffffff',
          },
        },
        MuiPickersCalendarHeader: {
          switchHeader: {
            backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
            color: isDark ? '#e4e7ec' : '#2B2F38',
          },
          dayLabel: {
            color: isDark ? '#a0aec0' : '#7A8496',
          },
          iconButton: {
            backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
            color: isDark ? '#a0aec0' : '#8A93A5',
          },
          transitionContainer: {
            color: isDark ? '#e4e7ec' : '#2B2F38',
          },
        },
        MuiPickersDay: {
          day: {
            color: isDark ? '#cbd5e0' : '#2B2F38',
            '&:hover': {
              backgroundColor: isDark ? '#334155' : '#e3f2fd',
            },
          },
          daySelected: {
            backgroundColor: isDark ? '#3b82f6' : '#1565C0',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: isDark ? '#2563eb' : '#0d47a1',
            },
          },
          dayDisabled: {
            color: isDark ? '#4b5563' : '#8C95A6',
          },
          current: {
            color: isDark ? '#60A5FA' : '#1565C0',
          },
        },
        MuiPickersYear: {
          root: {
            color: isDark ? '#cbd5e0' : '#2B2F38',
            '&:focus': {
              color: isDark ? '#60A5FA' : '#1565C0',
            },
          },
          yearSelected: {
            color: isDark ? '#60A5FA' : '#1565C0',
          },
          yearDisabled: {
            color: isDark ? '#4b5563' : '#8C95A6',
          },
        },
        MuiPickersMonth: {
          root: {
            color: isDark ? '#cbd5e0' : '#2B2F38',
            '&:focus': {
              color: isDark ? '#60A5FA' : '#1565C0',
            },
          },
          monthSelected: {
            color: isDark ? '#60A5FA' : '#1565C0',
          },
          monthDisabled: {
            color: isDark ? '#4b5563' : '#8C95A6',
          },
        },
        MuiPickersModal: {
          dialogRoot: {
            backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
          },
          dialog: {
            backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
          },
          withAdditionalAction: {
            '& .MuiDialogActions-root': {
              backgroundColor: isDark ? '#1e293b' : '#FFFFFF',
              borderTop: isDark ? '1px solid #334155' : '1px solid #e4e7ec',
            },
          },
        },
        MuiPickersClockNumber: {
          clockNumber: {
            color: isDark ? '#cbd5e0' : '#2B2F38',
          },
          clockNumberSelected: {
            color: '#ffffff',
          },
        },
        MuiPickersClock: {
          clock: {
            backgroundColor: isDark ? '#334155' : '#f5f5f5',
          },
          pin: {
            backgroundColor: isDark ? '#3b82f6' : '#1565C0',
          },
        },
        MuiPickersClockPointer: {
          pointer: {
            backgroundColor: isDark ? '#3b82f6' : '#1565C0',
          },
          thumb: {
            backgroundColor: isDark ? '#3b82f6' : '#1565C0',
            border: isDark ? '14px solid #3b82f6' : '14px solid #1565C0',
          },
          noPoint: {
            backgroundColor: isDark ? '#3b82f6' : '#1565C0',
          },
        },
      },
      spacing: factor => `${0.25 * factor}rem`,
      typography: {
        body1: {
          fontSize: '0.8rem'
        }
      },
      palette: {
        type: theme,
        primary: {
          main: isDark ? '#60A5FA' : '#4c90e3',
        },
        secondary: {
          main: isDark ? '#2196f3' : '#155DFC'
        },
        background: {
          default: isDark ? '#0f172a' : '#fafafa',
          paper: isDark ? '#1e293b' : '#ffffff',
        },
        text: {
          primary: isDark ? '#e4e7ec' : '#202939',
          secondary: isDark ? '#94a3b8' : '#667085',
        },
        divider: isDark ? '#334155' : '#e4e7ec',
      }
    });
  }, [theme]);

  const getAppRootComponent = () => {
    const vars = getUrlVars({ entryPoint: '' });
    switch (vars.entryPoint) {
      case 'pdf':
        return (
          <EditPdf user={user} />
        );
      default:
        return (
          <BrowserRouter>
            <App />
          </BrowserRouter>
        );
    }
  };

  return (
    <IntlProvider key={locale.code} locale={locale.code} messages={locale.messages}>
      <IntlSync />
      <ThemeProvider theme={appTheme}>
        <Notifier />
        <Suspense fallback={null}>
          {getAppRootComponent()}
        </Suspense>
      </ThemeProvider>
    </IntlProvider>
  );
};

export default RootElement;
